/**
 * Late-round timing for kickers and team defenses.
 *
 * These positions are real roster requirements, but their value is almost
 * entirely fungible until the rest of the lineup is built. This module keeps
 * that timing rule in one place so recommendation cards and the full board do
 * not disagree about when a specialist is actionable.
 *
 * Pure module — no I/O or DOM.
 */

import { upcomingPicks } from './availability.ts';
import type { DraftState, LeagueConfig, PlayerId, Position } from './types.ts';
import type { RosterNeed } from './valuation.ts';

export type SpecialistPolicyStage = 'disabled' | 'closed' | 'deferred' | 'preview' | 'required';

export interface SpecialistPolicy {
  stage: SpecialistPolicyStage;
  /** User selections remaining, including the current pick when applicable. */
  picks_remaining: number;
  /** Empty K and DEF starting slots on the user's roster. */
  specialist_slots_open: number;
  /** Picks remaining after reserving one pick for each open specialist slot. */
  discretionary_picks: number;
  open_positions: Position[];
}

export const SPECIALIST_POSITIONS: readonly Position[] = ['K', 'DEF'];

export function isSpecialistPosition(position: Position | null | undefined): boolean {
  return position === 'K' || position === 'DEF';
}

/**
 * Decide whether a specialist is deferred, previewed, or required now.
 *
 * The preview stage is intentionally one pick early: with one discretionary
 * pick left, the best needed specialist may occupy recommendation #5, but it
 * cannot displace the first four options. Once no discretionary pick remains,
 * specialists are released according to the model's normal order.
 */
export function specialistPolicyFor(
  draft: DraftState,
  league: LeagueConfig,
  need: RosterNeed,
): SpecialistPolicy {
  const openPositions = SPECIALIST_POSITIONS.filter(
    (position) => (need.open_starters[position] ?? 0) > 0,
  ) as Position[];
  const specialist_slots_open = openPositions.reduce(
    (total, position) => total + (need.open_starters[position] ?? 0),
    0,
  );
  const configuredSpecialistPositions = SPECIALIST_POSITIONS.filter(
    (position) => (league.starters_by_position[position] ?? 0) > 0,
  );

  if (
    league.league_type !== 'redraft' ||
    league.draft_type === 'auction' ||
    draft.my_draft_slot === null ||
    configuredSpecialistPositions.length === 0
  ) {
    return {
      stage: 'disabled',
      picks_remaining: 0,
      specialist_slots_open,
      discretionary_picks: 0,
      open_positions: openPositions,
    };
  }

  if (specialist_slots_open === 0) {
    return {
      stage: 'closed',
      picks_remaining: 0,
      specialist_slots_open,
      discretionary_picks: 0,
      open_positions: openPositions,
    };
  }

  const picks_remaining = upcomingPicks(
    draft.my_draft_slot,
    league.teams,
    league.rounds,
    league.draft_type,
    draft.current_pick_no,
  ).all.filter((pick) => pick >= draft.current_pick_no).length;
  const discretionary_picks = Math.max(0, picks_remaining - specialist_slots_open);

  return {
    stage: discretionary_picks > 1 ? 'deferred' : discretionary_picks === 1 ? 'preview' : 'required',
    picks_remaining,
    specialist_slots_open,
    discretionary_picks,
    open_positions: openPositions,
  };
}

function isOpenSpecialist(
  position: Position | null | undefined,
  policy: SpecialistPolicy,
): boolean {
  return position !== null && position !== undefined && isSpecialistPosition(position) && policy.open_positions.includes(position);
}

/**
 * Apply the policy to a scored recommendation list while preserving the
 * model's relative order everywhere the policy does not intervene.
 */
export function applySpecialistRecommendationPolicy<T>(
  items: T[],
  positionOf: (item: T) => Position | null | undefined,
  policy: SpecialistPolicy,
  limit: number,
): T[] {
  if (policy.stage === 'disabled' || limit <= 0) return items.slice(0, limit);

  const nonSpecialists = items.filter((item) => !isSpecialistPosition(positionOf(item)));
  const neededSpecialists = items.filter((item) => isOpenSpecialist(positionOf(item), policy));

  if (policy.stage === 'closed' || policy.stage === 'deferred') return nonSpecialists.slice(0, limit);

  if (policy.stage === 'preview') {
    const out = nonSpecialists.slice(0, Math.min(4, limit));
    if (neededSpecialists[0] && out.length < limit) out.push(neededSpecialists[0]);
    for (const item of nonSpecialists) {
      if (out.length >= limit) break;
      if (!out.includes(item)) out.push(item);
    }
    return out.slice(0, limit);
  }

  const allowed = new Set(neededSpecialists.slice(0, policy.specialist_slots_open));
  return items
    .filter((item) => !isSpecialistPosition(positionOf(item)) || allowed.has(item))
    .slice(0, limit);
}

/**
 * Reorder a complete board while leaving drafted/season-out rows in their
 * existing slots. Only available players are eligible for specialist moves.
 */
export function applySpecialistBoardPolicy<T>(
  items: T[],
  available: ReadonlySet<PlayerId>,
  idOf: (item: T) => PlayerId,
  positionOf: (item: T) => Position | null | undefined,
  policy: SpecialistPolicy,
): T[] {
  if (policy.stage === 'disabled') return [...items];

  const availableItems = items.filter((item) => available.has(idOf(item)));
  const nonSpecialists = availableItems.filter((item) => !isSpecialistPosition(positionOf(item)));
  const neededSpecialists = availableItems.filter((item) => isOpenSpecialist(positionOf(item), policy));
  const deferredSpecialists = availableItems.filter((item) => !neededSpecialists.includes(item) && isSpecialistPosition(positionOf(item)));

  let reorderedAvailable: T[];
  if (policy.stage === 'preview') {
    reorderedAvailable = [
      ...nonSpecialists.slice(0, 4),
      ...(neededSpecialists.slice(0, 1)),
      ...nonSpecialists.slice(4),
      ...neededSpecialists.slice(1),
      ...deferredSpecialists,
    ];
  } else if (policy.stage === 'required') {
    const allowed = new Set(neededSpecialists.slice(0, policy.specialist_slots_open));
    reorderedAvailable = [
      ...availableItems.filter((item) => !isSpecialistPosition(positionOf(item)) || allowed.has(item)),
      ...availableItems.filter((item) => isSpecialistPosition(positionOf(item)) && !allowed.has(item)),
    ];
  } else {
    reorderedAvailable = [...nonSpecialists, ...neededSpecialists, ...deferredSpecialists];
  }

  let availableIndex = 0;
  return items.map((item) => (available.has(idOf(item)) ? reorderedAvailable[availableIndex++]! : item));
}
